import PropTypes from 'prop-types'
import { Search, LayoutGrid, GitBranch, Layers, Eye } from 'lucide-react'

/**
 * Shared knowledge-graph control bar used by every surface (student `KnowledgeMap`, tutor
 * `EducatorKnowledgeMap`, admin `KnowledgeGraphTab`). It renders the common Layout + Edges toggles
 * and a filter row (Type / Domain / Difficulty / DOK / Status / Search), showing only the controls a
 * caller wires up so each role gets the filters applicable to it:
 *   - admin: Type, Domain, Difficulty, DOK, Search
 *   - student / tutor: Type, Domain, Difficulty, Status, Search
 *
 * The component is shape-agnostic: each control takes an explicit value + change handler (a control
 * is hidden when its handler is omitted). Selects use the `'all'` sentinel for "no filter"; callers
 * that store `null`/arrays adapt at the call site. Per-variant controls (Difficulty, DOK) are hidden
 * automatically in `skillMode`.
 */

const ALL_LAYOUT_MODES = [
  { id: 'domain', label: 'Domain', icon: LayoutGrid, description: 'Group by domain' },
  { id: 'difficulty', label: 'Difficulty', icon: Layers, description: 'Easy to Hard rows' },
  { id: 'hierarchical', label: 'Hierarchical', icon: GitBranch, description: 'Prerequisite chain' }
]

const EDGE_VISIBILITY_MODES = [
  { id: 'prerequisites', label: 'Prerequisites only' },
  { id: 'selected', label: 'Related on select' },
  { id: 'all', label: 'Show all' }
]

const SELECT_CLS =
  'text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent'

export default function KnowledgeGraphControls({
  layoutMode,
  onLayoutChange,
  layoutModes = ['domain', 'hierarchical'],
  edgeVisibility,
  onEdgeVisibilityChange,
  type,
  onTypeChange,
  domain,
  onDomainChange,
  domainOptions = [],
  difficulty,
  onDifficultyChange,
  dok,
  onDokChange,
  dokOptions = [],
  status,
  onStatusChange,
  statusOptions = [],
  search = '',
  onSearchChange,
  skillMode = false,
  extra = null
}) {
  const shownLayoutModes = ALL_LAYOUT_MODES.filter(
    (m) => layoutModes.includes(m.id) && !(skillMode && m.id === 'difficulty')
  )

  return (
    <div className="space-y-4">
      {/* Layout mode + edge visibility */}
      <div className="flex items-center gap-4 flex-wrap">
        {onLayoutChange && shownLayoutModes.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
              Layout:
            </span>
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              {shownLayoutModes.map((mode) => {
                const Icon = mode.icon
                const isActive = layoutMode === mode.id
                return (
                  <button
                    key={mode.id}
                    onClick={() => onLayoutChange(mode.id)}
                    title={mode.description}
                    aria-pressed={isActive}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{mode.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {onEdgeVisibilityChange && (
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Edges:</span>
            <select
              value={edgeVisibility || 'prerequisites'}
              onChange={(e) => onEdgeVisibilityChange(e.target.value)}
              className={SELECT_CLS}
              aria-label="Edge visibility"
            >
              {EDGE_VISIBILITY_MODES.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        {onTypeChange && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Type:</label>
            <select
              value={type || 'all'}
              onChange={(e) => onTypeChange(e.target.value)}
              className={SELECT_CLS}
              aria-label="Type"
            >
              <option value="all">All</option>
              <option value="math">Math</option>
              <option value="reading_writing">Reading &amp; Writing</option>
            </select>
          </div>
        )}

        {onDomainChange && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Domain:</label>
            <select
              value={domain || 'all'}
              onChange={(e) => onDomainChange(e.target.value)}
              className={`${SELECT_CLS} max-w-[180px]`}
              aria-label="Domain"
            >
              <option value="all">All Domains</option>
              {domainOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        )}

        {onDifficultyChange && !skillMode && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Difficulty:</label>
            <select
              value={difficulty || 'all'}
              onChange={(e) => onDifficultyChange(e.target.value)}
              className={SELECT_CLS}
              aria-label="Difficulty"
            >
              <option value="all">All</option>
              <option value="E">Easy</option>
              <option value="M">Medium</option>
              <option value="H">Hard</option>
            </select>
          </div>
        )}

        {onDokChange && !skillMode && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">DOK:</label>
            <select
              value={dok || 'all'}
              onChange={(e) => onDokChange(e.target.value)}
              className={SELECT_CLS}
              aria-label="DOK"
            >
              <option value="all">All</option>
              {dokOptions.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
        )}

        {onStatusChange && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
            <select
              value={status || 'all'}
              onChange={(e) => onStatusChange(e.target.value)}
              className={SELECT_CLS}
              aria-label="Status"
            >
              <option value="all">All</option>
              {statusOptions.map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {onSearchChange && (
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1 max-w-[300px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search id, skills, domains..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="Search"
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {extra}
      </div>
    </div>
  )
}

KnowledgeGraphControls.propTypes = {
  layoutMode: PropTypes.string,
  onLayoutChange: PropTypes.func,
  layoutModes: PropTypes.arrayOf(PropTypes.string),
  edgeVisibility: PropTypes.string,
  onEdgeVisibilityChange: PropTypes.func,
  type: PropTypes.string,
  onTypeChange: PropTypes.func,
  domain: PropTypes.string,
  onDomainChange: PropTypes.func,
  domainOptions: PropTypes.arrayOf(PropTypes.string),
  difficulty: PropTypes.string,
  onDifficultyChange: PropTypes.func,
  dok: PropTypes.string,
  onDokChange: PropTypes.func,
  dokOptions: PropTypes.arrayOf(PropTypes.string),
  status: PropTypes.string,
  onStatusChange: PropTypes.func,
  statusOptions: PropTypes.arrayOf(PropTypes.array),
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  skillMode: PropTypes.bool,
  extra: PropTypes.node
}
